import { defineStore } from 'pinia'
import Pusher from 'pusher-js'

import apiClient from '@/utils/api-client'

/**
 * Reverb speaks the Pusher protocol, so pusher-js connects to it directly. Its
 * host options take the place of a Pusher cluster.
 *
 * @returns {object} pusher-js options for the Reverb server.
 */
function reverbConnectionOptions() {
    const forceTLS = (import.meta.env.VITE_REVERB_SCHEME || 'https') === 'https'
    const port =
        Number(import.meta.env.VITE_REVERB_PORT) || (forceTLS ? 443 : 80)

    return {
        // pusher-js 8 refuses to construct without a cluster, so pass an empty
        // one. wsHost below is what the socket actually connects to.
        cluster: '',
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: port,
        wssPort: port,
        forceTLS,
        enableStats: false,
    }
}

/**
 * The one realtime connection for the signed-in account.
 *
 * The API broadcasts every account-scoped event on the private channel
 * `private-account-{ulid}`, which only that account's users may subscribe to.
 * This store opens a single Pusher connection, authorizes the channel through
 * the API's broadcasting endpoint (with the same session as every other API
 * call), and fans each event out to the handlers registered for it.
 *
 * Screens call `on()` when they mount and call the returned function when they
 * unmount. Events are not replayed: anything that changed while the socket was
 * down is lost, so `onResync()` tells screens when the connection is back and
 * they should refetch what they show.
 */
export const useAccountEventsStore = defineStore('account-events', () => {
    let pusher = null
    let channel = null
    let connectedAccountUlid = null
    let hasConnectedBefore = false

    // eventName -> Set of handlers. Kept across connections, so a screen that is
    // still mounted when the account changes keeps receiving events.
    const handlers = new Map()
    // eventName -> the function bound to the current channel for that event.
    const boundDispatchers = new Map()
    const resyncHandlers = new Set()

    /**
     * Bind one event on the current channel. The bound function fans out to the
     * current handlers, and does nothing once its connection has been replaced,
     * so a late event from the previous account is never delivered.
     */
    function bindToChannel(eventName) {
        const instance = pusher
        const dispatch = (data) => {
            if (pusher !== instance) return

            for (const handler of handlers.get(eventName) ?? []) {
                handler(data)
            }
        }

        boundDispatchers.set(eventName, dispatch)
        channel.bind(eventName, dispatch)
    }

    /** Pusher asks the API to sign each private channel subscription. */
    function authorizeChannel({ socketId, channelName }, callback) {
        apiClient
            .post('/broadcasting/auth', {
                socket_id: socketId,
                channel_name: channelName,
            })
            .then((response) => callback(null, response.data))
            .catch((error) => callback(error, null))
    }

    /**
     * The first connection has nothing to catch up on. Every later one follows
     * a drop, during which events may have been missed. A connection that has
     * been replaced by another account's connection does not resync anything.
     */
    function onConnected(instance) {
        if (pusher !== instance) return

        if (!hasConnectedBefore) {
            hasConnectedBefore = true
            return
        }

        for (const handler of resyncHandlers) {
            handler()
        }
    }

    /** Close the connection and forget it. Handlers stay registered. */
    function disconnect() {
        try {
            pusher?.disconnect()
        } catch {
            // Already closed.
        }

        pusher = null
        channel = null
        boundDispatchers.clear()
        connectedAccountUlid = null
        hasConnectedBefore = false
    }

    /**
     * Open the connection for an account, or keep the current one when it is
     * already open for that account. A null account closes the connection.
     *
     * @param {string|null} accountUlid
     * @returns {void}
     */
    function connect(accountUlid) {
        if (!accountUlid) {
            disconnect()
            return
        }

        if (pusher && connectedAccountUlid === accountUlid) return

        disconnect()

        const key = import.meta.env.VITE_REVERB_APP_KEY
        const host = import.meta.env.VITE_REVERB_HOST

        // Live updates are a convenience. Without them the screens still load
        // fresh data each time they open.
        if (!key || !host) return

        try {
            const instance = new Pusher(key, {
                ...reverbConnectionOptions(),
                channelAuthorization: { customHandler: authorizeChannel },
            })
            pusher = instance
            pusher.connection.bind('connected', () => onConnected(instance))

            channel = pusher.subscribe('private-account-' + accountUlid)
            for (const eventName of handlers.keys()) {
                bindToChannel(eventName)
            }

            connectedAccountUlid = accountUlid
        } catch {
            disconnect()
        }
    }

    /**
     * Run `handler` for each `eventName` the account broadcasts.
     *
     * @param {string} eventName
     * @param {(data: object) => void} handler
     * @returns {() => void} Removes this handler.
     */
    function on(eventName, handler) {
        if (!handlers.has(eventName)) {
            handlers.set(eventName, new Set())
        }

        const eventHandlers = handlers.get(eventName)
        const isFirstHandler = eventHandlers.size === 0

        eventHandlers.add(handler)

        if (channel && isFirstHandler) {
            bindToChannel(eventName)
        }

        return () => off(eventName, handler)
    }

    function off(eventName, handler) {
        const eventHandlers = handlers.get(eventName)
        if (!eventHandlers) return

        eventHandlers.delete(handler)

        if (eventHandlers.size > 0) return

        handlers.delete(eventName)
        channel?.unbind(eventName, boundDispatchers.get(eventName))
        boundDispatchers.delete(eventName)
    }

    /**
     * Run `handler` after the connection comes back from a drop, so the screen
     * can refetch what it may have missed.
     *
     * @param {() => void} handler
     * @returns {() => void} Removes this handler.
     */
    function onResync(handler) {
        resyncHandlers.add(handler)

        return () => resyncHandlers.delete(handler)
    }

    return {
        connect,
        disconnect,
        on,
        onResync,
    }
})
